import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SeoHead from "@/components/SeoHead";

export default function PrivacyPage() {
  const updated = "May 2026";
  return (
    <div className="min-h-screen topo-bg" data-testid="privacy-page">
      <SeoHead
        title="Privacy Policy"
        description="How SummaryMaps Journal collects, uses, and protects your data."
      />
      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-20 sm:pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-brand mb-8" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to the journal
        </Link>

        <div className="text-xs uppercase tracking-[0.2em] text-brand mb-3">Legal</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold leading-tight text-ink">Privacy Policy</h1>
        <p className="mt-4 text-sm text-ink-subtle">Last updated: {updated}</p>

        <div className="prose-editorial mt-10 sm:mt-12">
          <p>
            SummaryMaps Journal ("we", "us", or "the Journal") is a small editorial site that publishes essays about
            mindmaps, education, books, and new releases. We collect the minimum amount of data needed to run the
            site, send our newsletter, and understand how readers find us. This page explains exactly what is
            collected, why, and how to remove it.
          </p>

          <h2>What we collect</h2>
          <p>
            <strong>Newsletter subscribers.</strong> When you submit the newsletter form, we store your email address
            and the date you subscribed. That's it. We do not collect your name, location, or any other personal
            details.
          </p>
          <p>
            <strong>Analytics.</strong> We use Google Analytics 4 to understand which essays readers enjoy and where
            our traffic comes from. Google may set cookies and collect data such as your approximate location (from
            IP), device type, browser, referring URL, and pages viewed. Google Analytics anonymizes IPs by default.
          </p>
          <p>
            <strong>Advertising.</strong> We do not currently show display advertising on SummaryMaps Journal.
          </p>
          <p>
            <strong>Meta Pixel.</strong> We use the Meta Pixel (fbevents.js) to measure how readers find us from
            Facebook and Instagram. Meta may set cookies and collect anonymized event data such as pageviews.
          </p>
          <p>
            <strong>Chat widget.</strong> We use ManyChat to power the on-site chat widget. If you interact with it,
            ManyChat processes the messages you send on our behalf.
          </p>
          <p>
            <strong>Server logs.</strong> Like every website, our servers receive standard request data (IP address,
            user-agent, timestamp) when you visit a page. These logs are kept short-term for security and debugging.
          </p>
          <p>
            <strong>We do not collect.</strong> Names, addresses, phone numbers, payment information, or any sensitive
            categories of data. We have no user accounts for readers.
          </p>

          <h2>How we use it</h2>
          <ul>
            <li>To send you the weekly newsletter if you subscribed.</li>
            <li>To understand which essays resonate and improve the editorial mix.</li>
            <li>To measure how readers find us from Meta properties (Facebook/Instagram).</li>
            <li>To detect and prevent abuse (spam, scraping, etc.).</li>
          </ul>
          <p>We do not sell your data. We do not share it with third parties except the service providers below.</p>

          <h2>Third-party services</h2>
          <ul>
            <li><strong>Resend</strong> — delivers our newsletter emails. Your email address and the message content
              are processed by Resend on our behalf. See{" "}
              <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">Resend's privacy policy</a>.
            </li>
            <li><strong>Google Analytics (GA4)</strong> — website analytics. See{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's privacy policy</a>.
            </li>
            <li><strong>Meta Pixel</strong> — advertising measurement on Facebook/Instagram. See{" "}
              <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Meta's privacy policy</a>.
            </li>
            <li><strong>ManyChat</strong> — powers the on-site chat widget.</li>
            <li><strong>Cloudflare</strong> — DNS and traffic protection. Standard server logs only.</li>
            <li><strong>MongoDB Atlas</strong> — stores our articles and subscriber list.</li>
          </ul>

          <h2>Affiliate links</h2>
          <p>
            Some book links on the Journal go to Amazon (as an Amazon Associate we earn from qualifying purchases)
            and Bookshop.org. When you click those links, Amazon and Bookshop.org may set their own cookies and
            their own privacy policies apply.
          </p>

          <h2>Cookies</h2>
          <p>
            We do not set cookies of our own beyond what's needed to make the site work. Google Analytics and the
            Meta Pixel set their own cookies as described above. Most browsers let you block or delete cookies from
            settings; doing so will not break the site.
          </p>

          <h2>Your rights</h2>
          <p>
            Wherever you live, you can:
          </p>
          <ul>
            <li><strong>Unsubscribe</strong> from the newsletter at any time — every email contains a one-click unsubscribe link, or you can email us (below) and we'll remove you within 48 hours.</li>
            <li><strong>Request a copy</strong> of any data we hold about you.</li>
            <li><strong>Request deletion</strong> of your email and any associated records.</li>
          </ul>
          <p>
            If you are in the EU/UK, you have additional rights under GDPR. If you are in California, you have
            additional rights under the CCPA. To exercise any of these, email{" "}
            <a href="mailto:contact@summarymaps.com">contact@summarymaps.com</a>.
          </p>

          <h2>Data retention</h2>
          <p>
            Newsletter subscriber emails are kept until you unsubscribe or request deletion. Analytics data is kept
            for 14 months per our Google Analytics configuration. Server logs are kept for up to 30 days.
          </p>

          <h2>Children</h2>
          <p>
            The Journal is not directed at children under 13 and we do not knowingly collect data from them. If you
            believe a child has subscribed, contact us and we will delete the record.
          </p>

          <h2>Security</h2>
          <p>
            We take reasonable measures to protect the data we hold (encrypted connections, principle of least
            access, modern hosting). No online service can guarantee perfect security; if a breach affecting reader
            data ever occurs, we will notify affected subscribers promptly.
          </p>

          <h2>Changes to this policy</h2>
          <p>
            When this policy changes, we update the "Last updated" date at the top. Material changes will be
            announced at the top of the newsletter.
          </p>

          <h2>Contact</h2>
          <p>
            Questions, requests, or concerns? Email{" "}
            <a href="mailto:contact@summarymaps.com">contact@summarymaps.com</a>. We read every message.
          </p>
        </div>
      </main>
    </div>
  );
}
